import React from "react";
import { useMediaQuery } from "@chakra-ui/react";
import {
  AspectRatio,
  Box, 
  Button, 
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  Text
} from "@chakra-ui/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper";
import enableSwiperClicksFromNav from "../../helpers/enableSwiperClicksFromNav";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/a11y";
import "./banner-carousel.scss";
import { CarouselProps } from "../../types/homePageTypes";


const BannerCarousel = ({ title, isWebProgrammeSlider, sliderItems }: CarouselProps) => {
  const [isLargerThan1200] = useMediaQuery("(min-width: 1200px)");
  const { swiperRef } = enableSwiperClicksFromNav();

  const pagination = {
    clickable: true,
    renderBullet: function ( index: number, className: string ) {
      return `<span class=${ className }></span>`;
    },
  };

  // limit size of the number of slides
  const MAX_SLIDES = 9;
  let finalSliderItems = sliderItems;
  if (sliderItems.length > MAX_SLIDES) {
    finalSliderItems = sliderItems.slice(0, MAX_SLIDES);
  }

  const isThereVideo = finalSliderItems.some(( slide ) => slide.video);

  return (
    <Box marginTop="0 !important">
      <Heading as="h2" sx={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: '0',}}>
        { title ? title : 'Banner Carousel'}
      </Heading>

      {/* desktop */}
      {isLargerThan1200 &&
        <Swiper
          id="bannerCarousel"
          onSwiper={( swiperInstance ) => { swiperRef.current = swiperInstance }} // this is the only difference between mobile and desktop
          pagination={ pagination }
          autoplay={{
            delay: isThereVideo ? 20000 : 7000,
            disableOnInteraction: true
          }}
          loop={ true }
          modules={[ Autoplay, Pagination, A11y ]}
        >
          {finalSliderItems.map(( slide, index ) => {
              return (
                <SwiperSlide key={ index }>
                  {slide.video &&
                    <>
                      <Box
                        as="video"
                        w="100%"
                        h={{ base: "400px", xl: "800px", bd3: "1080px" }}
                        position="relative"
                        autoPlay
                        playsInline
                        loop
                        muted
                        objectFit="cover"
                        poster={ slide.posterUrl }
                      >
                        <source src={ slide.video } type="video/mp4" />
                        <Box
                          as="img"
                          w="100%"
                          h={{ base: "400px", xl: "800px", bd3: "1080px" }}
                          backgroundImage={ slide.posterUrl }
                          aria-label={ slide.posterAlt }
                          backgroundRepeat="no-repeat"
                          backgroundSize="cover"
                          backgroundPosition="center"
                        />
                      </Box>
                      <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        h="100%"
                        w="100%"
                        p={{ base: "30px", sm: "56px", xl: "90px" }}
                        direction="column"
                        justifyContent="flex-end !important"
                        alignItems="flex-start !important"
                        background="linear-gradient(rgba(0, 0, 0, 0.56) 0%, rgba(0, 0, 0, 0.553) 4.2%, rgba(0, 0, 0, 0.533) 7.4%, rgba(0, 0, 0, 0.5) 10%, rgba(0, 0, 0, 0.463) 12.2%, rgba(0, 0, 0, 0.416) 14.4%, rgba(0, 0, 0, 0.365) 16.8%, rgba(0, 0, 0, 0.31) 19.7%, rgba(0, 0, 0, 0.255) 23.5%, rgba(0, 0, 0, 0.2) 28.5%, rgba(0, 0, 0, 0.15) 35%, rgba(0, 0, 0, 0.1) 43.2%, rgba(0, 0, 0, 0.06) 53.5%, rgba(0, 0, 0, 0.027) 66.2%, rgba(0, 0, 0, 0.008) 81.6%, rgba(0, 0, 0, 0) 100%)"
                      >
                        {/* label */}
                        { slide.webProgramme.length > 0 &&
                          <Box marginBottom="18px">
                            {slide.webProgramme.map(( label, labelIndex ) => {
                                // allow only one tag
                                if (labelIndex > 0) {
                                  return null
                                }
                                return (
                                  <Text
                                    key={ labelIndex }
                                    w="fit-content"
                                    size={{ base: "program-tag-mobile", sm: "program-tag-tablet"  }}
                                    textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                                  >
                                    { label.title }
                                  </Text>
                                )
                              })
                            }
                          </Box>
                        }

                        {/* title */}
                        {slide.title &&
                          <Box
                            marginBottom={{ base: "8px", sm: "16px" }}
                            maxW={{ base: "100%", xl: "80%" }}
                          >
                            <Heading
                              as="h3"
                              size={{ base: "carousel-header-mobile", sm: "carousel-header-tablet", xl: "carousel-header-desktop" }}
                              textAlign="left"
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                              { slide.title }
                            </Heading>
                          </Box>
                        }

                        {/* description */}
                        {slide.description &&
                          <Box
                            marginBottom="16px"
                            maxW={{ base: "100%", xl: "75%" }}
                          >
                            <Text
                              size={{ base: "carousel-description-mobile", sm: "carousel-description" }}
                              dangerouslySetInnerHTML={{ __html: slide.description }}
                              noOfLines={{ base: 3 }}
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                            </Text>
                          </Box>
                        }

                        {/* button */}
                        {slide.buttonText &&
                          <LinkBox>
                            <Button
                              size={{ base: "carousel-mobile", sm: "carousel" }}
                              backgroundColor="#4B8510 !important"
                              _hover={{ bgColor: "#000000 !important", border: "none !important" }}
                            >
                              <LinkOverlay href={ slide.slug }>
                                { slide.buttonText }
                              </LinkOverlay>
                            </Button>
                          </LinkBox>
                        }
                      </Flex>
                    </>
                  }

                  {!slide.video &&
                    <AspectRatio
                      w="100%"
                      ratio={{ base: 375/400, xl: 1920/800 }}
                      h={{ base: "400px", xl: "800px", bd3: "1080px" }}
                      backgroundImage={ slide.posterUrl }
                      aria-label={ slide.posterAlt }
                      backgroundRepeat="no-repeat"
                      backgroundSize="cover"
                      backgroundPosition="center"
                    >
                      <Flex
                        p={{ base: "30px", sm: "56px", xl: "90px" }}
                        direction="column"
                        justifyContent="flex-end !important"
                        alignItems="flex-start !important"
                        background="linear-gradient(rgba(0, 0, 0, 0.56) 0%, rgba(0, 0, 0, 0.553) 4.2%, rgba(0, 0, 0, 0.533) 7.4%, rgba(0, 0, 0, 0.5) 10%, rgba(0, 0, 0, 0.463) 12.2%, rgba(0, 0, 0, 0.416) 14.4%, rgba(0, 0, 0, 0.365) 16.8%, rgba(0, 0, 0, 0.31) 19.7%, rgba(0, 0, 0, 0.255) 23.5%, rgba(0, 0, 0, 0.2) 28.5%, rgba(0, 0, 0, 0.15) 35%, rgba(0, 0, 0, 0.1) 43.2%, rgba(0, 0, 0, 0.06) 53.5%, rgba(0, 0, 0, 0.027) 66.2%, rgba(0, 0, 0, 0.008) 81.6%, rgba(0, 0, 0, 0) 100%)"
                      >
                        {/* label */}
                        { slide.webProgramme.length > 0 &&
                          <Box marginBottom="18px">
                            {slide.webProgramme.map(( label, labelIndex ) => {
                                // allow only one tag
                                if (labelIndex > 0) {
                                  return null
                                }
                                return (
                                  <Text
                                    key={ labelIndex }
                                    w="fit-content"
                                    size={{ base: "program-tag-mobile", sm: "program-tag-tablet"  }}
                                    textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                                  >
                                    { label.title }
                                  </Text>
                                )
                              })
                            }
                          </Box>
                        }

                        {/* title */}
                        {slide.title &&
                          <Box
                            marginBottom={{ base: "8px", sm: "16px" }}
                            maxW={{ base: "100%", xl: "80%" }}
                          >
                            <Heading
                              as="h3"
                              size={{ base: "carousel-header-mobile", sm: "carousel-header-tablet", xl: "carousel-header-desktop" }}
                              textAlign="left"
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                              { slide.title }
                            </Heading>
                          </Box>
                        }

                        {/* description */}
                        {slide.description &&
                          <Box
                            marginBottom="16px"
                            maxW={{ base: "100%", xl: "75%" }}
                          >
                            <Text
                              size={{ base: "carousel-description-mobile", sm: "carousel-description" }}
                              dangerouslySetInnerHTML={{ __html: slide.description }}
                              noOfLines={{ base: 3 }}
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                            </Text>
                          </Box>
                        }

                        {/* button */}
                        {slide.buttonText &&
                          <LinkBox>
                            <Button
                              size={{ base: "carousel-mobile", sm: "carousel" }}
                              backgroundColor="#4B8510 !important"
                              _hover={{ bgColor: "#000000 !important", border: "none !important" }}
                            >
                              <LinkOverlay href={ slide.slug }>
                                { slide.buttonText }
                              </LinkOverlay>
                            </Button>
                          </LinkBox>
                        }
                      </Flex>
                    </AspectRatio>
                  }
                </SwiperSlide>
              )
            })
          }  
        </Swiper>
      }

      {/* mobile */}
      {!isLargerThan1200 &&
        <Swiper
          id="bannerCarousel"
          pagination={ pagination }
          autoplay={{
            delay: isThereVideo ? 20000 : 7000,
            disableOnInteraction: true
          }}
          loop={ true }
          modules={[ Autoplay, Pagination, A11y ]}
        >
          {finalSliderItems.map(( slide, index ) => {
              return (
                <SwiperSlide key={ index }>
                  {slide.video &&
                    <>
                      <Box
                        as="video"
                        w="100%"
                        h={{ base: "400px", sm: "800px" }}
                        position="relative"
                        autoPlay
                        playsInline
                        loop
                        muted
                        objectFit="cover"
                        poster={ slide.posterUrl }
                      >
                        <source src={ slide.video } type="video/mp4" />
                        <Box
                          as="img"
                          w="100%"
                          h={{ base: "400px", sm: "800px" }}
                          backgroundImage={ slide.posterUrl }
                          aria-label={ slide.posterAlt }
                          backgroundRepeat="no-repeat"
                          backgroundSize="cover"
                          backgroundPosition="center"
                        />
                      </Box>
                      <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        h="100%"
                        w="100%"
                        p={{ base: "30px", sm: "56px", xl: "90px" }}
                        direction="column"
                        justifyContent="flex-end !important"
                        alignItems="flex-start !important"
                        background="linear-gradient(rgba(0, 0, 0, 0.56) 0%, rgba(0, 0, 0, 0.553) 4.2%, rgba(0, 0, 0, 0.533) 7.4%, rgba(0, 0, 0, 0.5) 10%, rgba(0, 0, 0, 0.463) 12.2%, rgba(0, 0, 0, 0.416) 14.4%, rgba(0, 0, 0, 0.365) 16.8%, rgba(0, 0, 0, 0.31) 19.7%, rgba(0, 0, 0, 0.255) 23.5%, rgba(0, 0, 0, 0.2) 28.5%, rgba(0, 0, 0, 0.15) 35%, rgba(0, 0, 0, 0.1) 43.2%, rgba(0, 0, 0, 0.06) 53.5%, rgba(0, 0, 0, 0.027) 66.2%, rgba(0, 0, 0, 0.008) 81.6%, rgba(0, 0, 0, 0) 100%)"
                      >
                        {/* label */}
                        { slide.webProgramme.length > 0 &&
                          <Box marginBottom={{ base: "16px", sm: "21px" }}>
                            {slide.webProgramme.map(( label, labelIndex ) => {
                                // allow only one tag
                                if (labelIndex > 0) {
                                  return null
                                }
                                return (
                                  <Text
                                    key={ labelIndex }
                                    w="fit-content"
                                    size={{ base: "program-tag-mobile", sm: "program-tag-tablet"  }}
                                    textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                                  >
                                    { label.title }
                                  </Text>
                                )
                              })
                            }
                          </Box>
                        }

                        {/* title */}
                        {slide.title &&
                          <Box
                            marginBottom={{ base: "8px", sm: "16px" }}
                            maxW={{ base: "100%", xl: "80%" }}
                          >
                            <Heading
                              as="h3"
                              size={{ base: "carousel-header-mobile", sm: "carousel-header-tablet", xl: "carousel-header-desktop" }}
                              textAlign="left"
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                              { slide.title }
                            </Heading>
                          </Box>
                        }

                        {/* description */}
                        {slide.description &&
                          <Box
                            marginBottom="16px"
                            maxW={{ base: "100%", xl: "75%" }}
                          >
                            <Text
                              size={{ base: "carousel-description-mobile", sm: "carousel-description" }}
                              dangerouslySetInnerHTML={{ __html: slide.description }}
                              noOfLines={{ base: 3 }}
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                            </Text>
                          </Box>
                        }

                        {/* button */}
                        {slide.buttonText &&
                          <LinkBox>
                            <Button
                              size={{ base: "carousel-mobile", sm: "carousel" }}
                              backgroundColor="#4B8510 !important"
                              _hover={{ bgColor: "none" }}
                            >
                              <LinkOverlay href={ slide.slug }>
                                { slide.buttonText }
                              </LinkOverlay>
                            </Button>
                          </LinkBox>
                        }
                      </Flex>
                    </>
                  }
                  {!slide.video &&
                    <AspectRatio
                      w="100%"
                      ratio={{ base: 375/400, sm: 750/800, xl: 1920/800 }}
                      h={{ base: "400px", sm: "800px" }}
                      backgroundImage={ slide.posterUrl }
                      aria-label={ slide.posterAlt }
                      backgroundRepeat="no-repeat"
                      backgroundSize="cover"
                      backgroundPosition="center"
                    >
                      <Flex
                        p={{ base: "30px", sm: "56px", xl: "90px" }}
                        direction="column"
                        justifyContent="flex-end !important"
                        alignItems="flex-start !important"
                        background="linear-gradient(rgba(0, 0, 0, 0.56) 0%, rgba(0, 0, 0, 0.553) 4.2%, rgba(0, 0, 0, 0.533) 7.4%, rgba(0, 0, 0, 0.5) 10%, rgba(0, 0, 0, 0.463) 12.2%, rgba(0, 0, 0, 0.416) 14.4%, rgba(0, 0, 0, 0.365) 16.8%, rgba(0, 0, 0, 0.31) 19.7%, rgba(0, 0, 0, 0.255) 23.5%, rgba(0, 0, 0, 0.2) 28.5%, rgba(0, 0, 0, 0.15) 35%, rgba(0, 0, 0, 0.1) 43.2%, rgba(0, 0, 0, 0.06) 53.5%, rgba(0, 0, 0, 0.027) 66.2%, rgba(0, 0, 0, 0.008) 81.6%, rgba(0, 0, 0, 0) 100%)"

                      >
                        {/* label */}
                        { slide.webProgramme.length > 0 &&
                          <Box marginBottom={{ base: "16px", sm: "21px" }}>
                            {slide.webProgramme.map(( label, labelIndex ) => {
                                // allow only one tag
                                if (labelIndex > 0) {
                                  return null
                                }
                                return (
                                  <Text
                                    key={ labelIndex }
                                    w="fit-content"
                                    size={{ base: "program-tag-mobile", sm: "program-tag-tablet"  }}
                                    textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                                  >
                                    { label.title }
                                  </Text>
                                )
                              })
                            }
                          </Box>
                        }

                        {/* title */}
                        {slide.title &&
                          <Box
                            marginBottom={{ base: "8px", sm: "16px" }}
                            maxW={{ base: "100%", xl: "80%" }}
                          >
                            <Heading
                              as="h3"
                              size={{ base: "carousel-header-mobile", sm: "carousel-header-tablet", xl: "carousel-header-desktop" }}
                              textAlign="left"
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                              { slide.title }
                            </Heading>
                          </Box>
                        }

                        {/* description */}
                        {slide.description &&
                          <Box
                            marginBottom="16px"
                            maxW={{ base: "100%", xl: "75%" }}
                          >
                            <Text
                              size={{ base: "carousel-description-mobile", sm: "carousel-description" }}
                              dangerouslySetInnerHTML={{ __html: slide.description }}
                              noOfLines={{ base: 3 }}
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            >
                            </Text>
                          </Box>
                        }

                        {/* button */}
                        {slide.buttonText &&
                          <LinkBox>
                            <Button
                              size={{ base: "carousel-mobile", sm: "carousel" }}
                              backgroundColor="#4B8510 !important"
                              _hover={{ bgColor: "none" }}
                            >
                              <LinkOverlay href={ slide.slug }>
                                { slide.buttonText }
                              </LinkOverlay>
                            </Button>
                          </LinkBox>
                        }
                      </Flex>
                    </AspectRatio>
                  }
                </SwiperSlide>
              )
            })
          }  
        </Swiper>
      }
    </Box>
  );
}

export default BannerCarousel;